import React, { useContext, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useNavigate } from 'react-router-dom'

// utils
import {
  advanceIsDisabled,
  hasActiveMembership
} from 'utils/membershipFunction'
import format from 'date-fns/format'

// API
import { getCreditLines } from 'api/credit-score'

// components
import PrimaryButton from 'components/primaryButton'
import List, { ListItem } from 'components/List'
import CardButton, { NewCardButton } from 'components/CardButtons'

// contexts
import { MovementsContext } from 'contexts/movements'
import { MeContext } from 'contexts/me'

// constants
import { ACT_TYPES } from 'constants/activities'
import { toTitleCase } from 'utils'

// assets
import {
  CardPurchaseIcon,
  MovementAdvanceIcon,
  TransferReceived,
  TransferSent
} from 'assets/icons'
import Thunderbolt from '../assets/images/emojis/thunderbolt.png'

import HubInformationModal from './HubInformationModal'

const {
  BANK_TRANSFER_IN,
  BANK_TRANSFER_OUT,
  MANUAL_MOVEMENT,
  CARD_PURCHASE,
  EXTRACASH_TYPE
} = ACT_TYPES

function getMovementType(slug) {
  switch (slug) {
    case BANK_TRANSFER_OUT:
      return <TransferSent height={140} />
    case BANK_TRANSFER_IN:
      return <TransferReceived height={140} />
    case MANUAL_MOVEMENT:
      return <MovementAdvanceIcon height={140} />
    case CARD_PURCHASE:
      return <CardPurchaseIcon height={140} />
    case EXTRACASH_TYPE:
      return <CardPurchaseIcon height={140} />
    default:
      break
  }
}

const MovementsViewDetail = () => {
  const [creditLine, setCreditLine] = useState(null)
  const [openHubInformationModal, setOpenHubInformationModal] = useState(false)
  const { currentActivityDetails: activity, isSettingCurrentActivity } =
    useContext(MovementsContext)
  const { me } = useContext(MeContext)
  const navigate = useNavigate()

  const movementType = getMovementType(activity?.slug)

  useEffect(() => {
    if (
      me &&
      hasActiveMembership(me) &&
      (activity?.slug === CARD_PURCHASE || activity?.slug === EXTRACASH_TYPE)
    )
      getCreditLines().then((res) => {
        if (res.success) setCreditLine(res.data.result[0])
      })
  }, [me, activity])

  return (
    <>
      <div className='flex flex-col items-center text-black mt-5'>
        {isSettingCurrentActivity ? (
          <>
            <Skeleton height={40} width={250} />
            <Skeleton height={40} width={92} className='mb-1' />
            <Skeleton height={140} circle width={140} className='mb-1' />
          </>
        ) : (
          <>
            <p className='font-normal text-center px-3 text-[1.875rem] leading-7 mb-1 [overflow-wrap:anywhere]'>
              {toTitleCase(activity?.title)}
            </p>
            <p className='text-4xl text-center text-color-black font-bold'>
              {activity?.amount}
            </p>
            <p className='opacity-70 text-center leading-5 font-normal text-xs'>
              {activity?.movementType}
            </p>

            <div className='text-xs text-black opacity-70 mb-5'>
              {activity?.slug === BANK_TRANSFER_OUT && (
                <p>(Transferencia enviada)</p>
              )}
              {activity?.slug === BANK_TRANSFER_IN && (
                <p>(Transferencia recibida)</p>
              )}
            </div>
            {movementType}
          </>
        )}
      </div>

      {(activity?.isAdvance && hasActiveMembership(me?.subscriber_products)) ||
        (activity?.isCashbackRefund && (
          <NewCardButton
            className='my-9'
            onClick={() => navigate('/suscripcion')}
          >
            <p className='font-bold text-purple-900'>
              Usaste tu membresía Wayni<span className='text-green-800'>+</span>
            </p>
            <p className='text-medium text-xs text-black'>
              Seguí conociendo todos tus beneficios.
            </p>
          </NewCardButton>
        ))}

      <List>
        {isSettingCurrentActivity ? (
          <>
            <div className='border-gray-500 border-b border-solid py-4'>
              <Skeleton height={30} />
            </div>
            <div className='border-gray-500 py-4'>
              <Skeleton height={30} />
            </div>
          </>
        ) : (
          activity &&
          Object.entries(activity?.items).map((item) => {
            if (!item[1] || item[1] === '') return
            return <ListItem key={item[1]} name={item[0]} value={item[1]} />
          })
        )}
      </List>

      {activity?.isAdvance ? (
        <>
          {!hasActiveMembership(me?.subscriber_products) ? (
            <CardButton
              activeUpperMessage='🔥 Suscribite a Wayni+ para volver a utilizar este beneficio'
              dataTestid='creditProfile'
              icon={Thunderbolt}
              title='Adelantos en tu tarjeta'
              description='Hacé click para suscribirte'
              className='mb-4 w-full'
              onClick={() => {
                navigate('/suscripcion')
              }}
            />
          ) : null}
          <PrimaryButton
            buttonText='Ver mis adelantos'
            primaryButtonFunction={() => navigate('/mis-adelantos')}
            className='mt-8'
          />
        </>
      ) : null}

      {creditLine ? (
        <CardButton
          disabled={advanceIsDisabled(creditLine)}
          icon={Thunderbolt}
          inactiveMessage='Se desbloqueará una vez renovada tu línea de crédito'
          title='Comprá ahora y pagá después'
          description='Hacé click para tener tu Adelanto'
          className='mb-4 w-full'
          onClick={() => {
            if (advanceIsDisabled(creditLine)) {
              setOpenHubInformationModal(true)
            } else {
              navigate('/wayni-credito')
            }
          }}
        />
      ) : null}

      {/* MODAL */}
      <HubInformationModal
        isOpen={openHubInformationModal}
        setIsOpen={setOpenHubInformationModal}
        canTakeAdvance={!advanceIsDisabled(creditLine)}
        expirationDate={
          creditLine?.credit_line_expiration
            ? format(new Date(creditLine?.credit_line_expiration), 'dd/MM')
            : 'el mes siguiente'
        }
      />
    </>
  )
}

export default MovementsViewDetail
